import Vue from 'vue';
import { i18n } from 'i18next';
import { $bus } from '@/HostApp/services/Bus';
import { Getter, Mutation, State } from 'vuex-simple';
import { BaseModule } from '@/HostApp/store/ApsModules/BaseModule';
import { baseApiUrl } from '@/Common/Environment/const';
import WsServiceModule, { CustomEventListener } from '@/HostApp/services/WsService/WsServiceModule';
import notificationsLS from '@/HostApp/services/NotificationsLocalStorage/NotificationsLocalStorage';
import httpServiceModule from '@/HostApp/services/HttpService/HttpServiceModule';
import i18nServiceModule from '@/HostApp/services/I18nService/I18nServiceModule';

const DEFAULT_RETRY_TIMEOUT = 5000;

interface IChannelData {
	id: string;
}

class MessageEventListener extends CustomEventListener {
	public call(event: MessageEvent): void {
		try {
			const message = JSON.parse(event.data);
			super.call(message);
		} catch (e) {
			// eslint-disable-next-line no-console
			console.warn('MessageEventListener error: ', e);
		}
	}
}

export class NotificationsModule extends BaseModule {
	private readonly restNotificationsEndpoint = `${baseApiUrl}/services/notification-manager/notifications`;

	protected messageSubscriptions = new MessageEventListener();

	public api = {
		addEventListener: (cb: (message: APS.INotification) => void): void => this.addEventListenerIfNew(cb),
		removeEventListener: (cb: (message: APS.INotification) => void): void => this.removeEventListener(cb)
	};

	private propagateEventsToVueApps(message: APS.INotification): void {
		$bus.$emit('apps-collector-emit-notifications', { message });
	}

	private addEventListenerIfNew(cb: (message: APS.INotification) => void): void {
		this.messageSubscriptions.addEventListenerIfNew(cb);
	}

	private async onNewMessage(event: MessageEvent): Promise<void> {
		const message = JSON.parse(event.data);

		await this.loadTranslations(message);

		this.updateMessage(message);
		this.messageSubscriptions.call(event);
	}

	private removeEventListener(cb: Common.FnAny): void {
		this.messageSubscriptions.removeEventListener(cb);
	}

	private getUserId(): string {
		return window.aps.context.user.aps.id;
	}

	public markAsRead(id: string): void {
		if (this.messages[id]) {
			this.messages[id].readStatus = notificationsLS.setReadStatus(this.getUserId(), id, 'read');
		}
	}

	public markAllAsRead(): void {
		//Removing the ones not on the screen
		notificationsLS.markAllAsRead(this.getUserId());

		//Processing the ones on the screen
		this.notificationArray.forEach((notification) => {
			this.markAsRead(notification.id);
		});
	}

	public async runService(isFirstRun: boolean = true): Promise<void> {
		if (isFirstRun) {
			this.api.addEventListener((message: APS.INotification) => this.propagateEventsToVueApps(message));
			notificationsLS.convertOldReadStatusesOnce(this.getUserId());
		}

		const channelId = await this.getChannel();

		await this.refreshDisplayMessages(isFirstRun);

		if (!channelId) {
			this.retry();
			return;
		}

		// LocalStorage config for debug purpose
		if (localStorage.getItem('notifications_skipWS') === null) {
			try {
				await this.runWebSockets(channelId);
				// In case of normal closure we do not switch to polling or retry.
				return;
			} catch (event) {
				this.logError('WS Notifications: Error on connection.', event);
			}
		} else {
			this.logStatus('Notifications: Skipped WS due to local storage flag notifications_skipWS', 'log');
		}

		// LocalStorage config for debug purpose
		if (localStorage.getItem('notifications_skipLP') === null) {
			try {
				await this.runLongPolling(channelId);
			} catch (error) {
				this.logError('Long-Polling Notifications: Error on connection.', error);
			}
		} else {
			this.logStatus('Notifications: Skipped LP due to local storage flag notifications_skipLP', 'log');
		}

		this.retry();
	}

	protected retry(): void {
		setTimeout(() => {
			this.logStatus('Notifications: Retrying to connect...', 'log');
			this.runService(false);
		}, DEFAULT_RETRY_TIMEOUT);
	}

	protected async getChannel(): Promise<string | null> {
		const channelData = await this.connectToNotificationsChannel();

		if (channelData.id) {
			return channelData.id;
		}

		return null;
	}

	protected async runWebSockets(channelId: string): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			this.logStatus('WS Notifications: Connecting', 'log');

			const wsClient = WsServiceModule.getOrCreateWS('ws/notifications/channel');

			// Open/close logging handled by wsClient.
			wsClient.onOpen.addEventListenerIfNew((): void => {
				const command = { channelId, command: 'CONNECT' };
				wsClient.send(command);
			});
			wsClient.onMessage.addEventListenerIfNew((event: MessageEvent) => this.onNewMessage(event));
			wsClient.onClose.addEventListenerIfNew((event: CloseEvent): void => {
				if (event.wasClean) {
					resolve();
					return;
				}
				reject(event);
			});

			if (!wsClient.connected) {
				await wsClient.start();
			}
		});
	}

	protected async runLongPolling(channelId: string): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			this.logStatus(`Long-Polling Notifications: Connecting to channel: ${channelId}`, 'log');

			try {
				const notifications = await this.getFromNotificationsChannel(channelId);

				this.runLongPolling(channelId)
					.then(() => resolve())
					.catch((err) => reject(err));

				if (notifications.length) {
					notifications.forEach((notification: APS.INotification): void => {
						this.onNewMessage(new MessageEvent('MessageEvent', { data: JSON.stringify(notification) }));
					});
				}
			} catch (err) {
				reject(err);
			}
		});
	}

	protected loadTranslations(
		notifications: Array<APS.INotificationFromApsBus> | APS.INotificationFromApsBus
	): Promise<Array<void | i18n>> {
		const tService = i18nServiceModule;

		const arrayOfNotifications = [notifications].flat();

		const packages = arrayOfNotifications.reduce((acc, notification) => {
			notification.packageId && acc.add(notification.packageId);

			return acc;
		}, new Set() as Set<string>);

		return tService.loadPackageTranslations(Array.from(packages));
	}

	protected async refreshDisplayMessages(setAllMessages: boolean): Promise<void> {
		const displayMessages = await this.getDisplayMessages();

		const receivedMessages = [
			...displayMessages.inProgressActivities,
			...displayMessages.otherActivities,
			...displayMessages.warnings
		]
			.map((m) => m as APS.INotification)
			.reduce((acc, message) => {
				message.readStatus = notificationsLS.getReadStatus(this.getUserId(), message.id);
				acc[message.id] = message;
				return acc;
			}, {} as Common.IMap<APS.INotification>);

		if (setAllMessages) {
			const notifications = Object.keys(receivedMessages).map((key) => receivedMessages[key]);
			await this.loadTranslations(notifications);
			this.messages = receivedMessages;
		} else {
			//Imitating receiving new messages by calculating diff of the prev and new message arrays.
			const newMessages = Object.values(receivedMessages).filter((msg) => !this.messages[msg.id]);
			const messagesWithChangedStatus = Object.values(this.messages).filter(
				(msg) => receivedMessages[msg.id] && receivedMessages[msg.id].status !== msg.status
			);

			[...newMessages, ...messagesWithChangedStatus].forEach((msg: APS.INotification) => {
				this.onNewMessage(new MessageEvent('MessageEvent', { data: JSON.stringify(msg) }));
			});
		}
	}

	protected async connectToNotificationsChannel(): Promise<IChannelData> {
		return (await httpServiceModule.get<IChannelData>(`${this.restNotificationsEndpoint}/channel`)).data;
	}

	protected async getFromNotificationsChannel(channelId: string): Promise<Array<APS.INotification>> {
		return (
			await httpServiceModule.get<Array<APS.INotification>>(
				`${this.restNotificationsEndpoint}?channel=${channelId}&${Date.now()}`
			)
		).data;
	}

	protected async getDisplayMessages(): Promise<APS.INotificationResponse> {
		return (
			await httpServiceModule.get<APS.INotificationResponse>(`${this.restNotificationsEndpoint}/displayMessages`)
		).data;
	}

	protected logStatus(message: string, type: 'warn' | 'log'): void {
		// eslint-disable-next-line no-console
		console[type](message);
	}

	protected logError(message: string, error: unknown): void {
		// eslint-disable-next-line no-console
		console.error(message, error);
	}

	@Mutation()
	protected updateMessage(notification: APS.INotification): void {
		notification.readStatus = notificationsLS.setReadStatus(this.getUserId(), notification.id, 'unread');
		Vue.set(this.messages, notification.id, notification);
	}

	@State()
	public messages: Common.IMap<APS.INotification> = {};

	@Getter()
	protected get notificationArray(): Array<APS.INotification> {
		return Object.values(this.messages);
	}

	@Getter()
	public get activeNotifications(): Array<APS.INotification> {
		return this.notificationArray.filter((message) => !message.resolved);
	}

	@Getter()
	public get resolvedNotifications(): Array<APS.INotification> {
		return this.notificationArray.filter((message) => message.resolved);
	}

	@Getter()
	public get sortedNotifications(): Array<APS.INotification> {
		return this.activeNotifications.sort((a, b) => {
			const getLastDate = (value: APS.INotification): Date => {
				const created = new Date(value.created);
				const modified = new Date(value.modified);

				return modified > created ? modified : created;
			};

			const dataA = getLastDate(a);
			const dataB = getLastDate(b);

			//@ts-ignore
			return dataB - dataA;
		});
	}
}

const notificationsModule = new NotificationsModule();

export default notificationsModule;
